/*
 * @providesModule SMSVerification
 */
import AWSManager from 'AWSManager';
import {_m} from 'INTL';
import NavigationBar from 'NavigationBar';
import SMSVerifyController from 'SMSVerifyController';
import SkillzImage from 'SkillzImage';
import {
  ACCOUNT_SMS_VERIFY_AREACODE_TAP,
  ACCOUNT_SMS_VERIFY_CALL_SEND,
  ACCOUNT_SMS_VERIFY_ENTER,
  ACCOUNT_SMS_VERIFY_PROMPT,
  ACCOUNT_SMS_VERIFY_SEND,
} from 'AWSConstants';
import React, {useCallback, useEffect, useState} from 'react';
import SkillzStyle from 'SkillzStyle';
import {BackHandler, StyleSheet, TouchableOpacity, View} from 'react-native';
import {SMSVerify} from 'SMSVerify';
import WithdrawFlowController from 'WithdrawFlowController';

import styles, {backgroundColor} from './SMSVerification.style';
import {trackTelemetryEvent} from 'TrackTelemetryEvent';
import {TelemetryEventName} from 'TelemetryConstants';

const {textColorPrimary} = SkillzStyle.fullStyles;

type Props = {
  onClose: () => void;
  clientProps?: {
    header: string;
    body: string;
  };
  fromWithdraw?: boolean;
  showCloseButton: boolean;
  rightIconComponent?: React.ReactNode;
  onDone: () => void;
  onPhoneNumberInUse?: (phoneNumber: string, country: string, countryValue: string) => void;
  disableEdit?: boolean;
  phoneNumber?: string;
  phoneNumberWithCountryCode?: string;
};
const AWS_EVENTS = {
  AREA_CODE_BOX_PRESS: 'ACCOUNT_SMS_VERIFY_AREACODE_TAP',
  SEND_CODE: 'ACCOUNT_SMS_VERIFY_SEND',
  SEND_CODE_CALL: 'ACCOUNT_SMS_VERIFY_CALL_SEND',
  ENTER_CODE: 'ACCOUNT_SMS_VERIFY_ENTER',
  SMS_PROMPT: 'ACCOUNT_SMS_VERIFY_PROMPT',
};

const SMSVerification = ({
  onClose,
  clientProps = {
    header: _m('Verify your phone number'),
    body: _m('This extra layer of security helps prevent unauthorized access to your account.'),
  },
  showCloseButton,
  rightIconComponent,
  onDone: onDoneProps,
  fromWithdraw,
  onPhoneNumberInUse,
  disableEdit = false,
  phoneNumber = undefined,
  phoneNumberWithCountryCode = undefined,
}: Props) => {
  const [verificationCode, setVerificationCode] = useState(false);
  useEffect(() => {
    SMSVerifyController.incrementPromptCount();
    AWSManager.trackEvent(ACCOUNT_SMS_VERIFY_PROMPT);
    trackTelemetryEvent({
      event: TelemetryEventName.WITHDRAW_VERIFY_PHONE_SHOWN,
      includeUserWalletDetails: true,
    });
    // override android back button
    BackHandler.addEventListener('hardwareBackPress', onBackButtonPress);
    return () => {
      BackHandler.removeEventListener('hardwareBackPress', onBackButtonPress);
    };
  }, []);

  const onDone = useCallback(() => {
    if (fromWithdraw) {
      WithdrawFlowController._displayWithdrawFlowFromSMS();
      return;
    }
    if (onDoneProps) onDoneProps();
  }, [onDoneProps]);

  // override android back button
  const onBackButtonPress = (): boolean => {
    if (showCloseButton) {
      onClose();
    }
    return true;
  };
  const confirmVerification = (
    code: string,
    phoneNumber: string,
    onSuccess: () => void,
    onFailure: (error: string) => void
  ) => {
    SMSVerifyController.customConfirmVerification(code, phoneNumber, onSuccess, onFailure, onDone);
  };

  const renderRightIcon = () => {
    if (rightIconComponent) {
      return rightIconComponent;
    }
    return (
      <TouchableOpacity style={styles.rightIcon} testID={'closeButton'} onPress={onDone}>
        <SkillzImage
          source={'close_button_icon.svg'}
          style={styles.rightIcon}
          tintColor={textColorPrimary.color}
        />
      </TouchableOpacity>
    );
  };

  return (
    <View style={[{backgroundColor}, StyleSheet.absoluteFill]}>
      <NavigationBar
        hasBackButton={showCloseButton}
        onBackButtonPress={() => {
          {
            if (verificationCode) {
              setVerificationCode(false);
            } else {
              onClose();
            }
          }
        }}
        title={_m('Secure Your Account')}
        titleStyle={styles.titleStyle}
        hasRightComponent={showCloseButton}
        renderRightComponent={renderRightIcon()}
      />
      <SMSVerify
        containerStyle={styles.smsContainerStyle}
        messageText={clientProps}
        isValidNumber={SMSVerifyController.isValidNumber}
        sendSMSVerification={
          phoneNumber
            ? SMSVerifyController.requestVerificationSignIn
            : SMSVerifyController.requestVerificationSignup
        }
        fetchCountry={SMSVerifyController.fetchCountry}
        formatNumber={SMSVerifyController.formatNumber}
        getCountryCode={phoneNumberWithCountryCode ? () => ' ' : SMSVerifyController.getCountryCode}
        getFlag={SMSVerifyController.getFlag}
        countryData={SMSVerifyController.getCountryData()}
        numberOfInputs={5}
        codeValidation={confirmVerification}
        showTosMessage={false}
        showSmsMarketingCheckbox={true}
        events={{
          countrySelect: ACCOUNT_SMS_VERIFY_AREACODE_TAP,
          callRequest: ACCOUNT_SMS_VERIFY_CALL_SEND,
          codeRequest: ACCOUNT_SMS_VERIFY_SEND,
          codeEnter: ACCOUNT_SMS_VERIFY_ENTER,
        }}
        trackEvent={(name) => AWSManager.trackEvent(name)}
        verificationCodeValues={{verificationCode, setVerificationCode}}
        inputPhoneNumber={phoneNumber}
        disableEdit={disableEdit}
        inputCountry={phoneNumberWithCountryCode ? '+' : undefined}
        inputCountryValue={phoneNumberWithCountryCode ? '+' : undefined}
        onPhoneNumberInUse={onPhoneNumberInUse}
      />
    </View>
  );
};

export default SMSVerification;
